import {
  setGuaranteesFulfilledAction,
  setGuaranteesPendingAction,
  setGuaranteesRejectedAction,
} from './actions/actions.ts';
import { initialState } from './initialState.ts';
import type {
  TGuaranteesApplication,
  TGuaranteesBaseRelations,
  TGuaranteesBeneficiaryResponse,
  TGuaranteesExecutionForm,
  TGuaranteesForm,
  TGuaranteesIssueGuaranteeDetails,
  TGuaranteesMainDealConditions,
  TGuaranteesManagerTerms,
  TGuaranteesPageStore,
  TGuaranteesPrincipal,
  TFetchGuaranteesMainDealsRequest,
} from './types.ts';
import {
  buildGuaranteesMainDealsRequestKey,
  isGuaranteesApplicationPayload,
  normalizeGuaranteesBeneficiaryResponse,
  preserveGuaranteesBeneficiaryCountryNames,
  updateCurrentGuaranteesApplicationVersion,
} from './utils.ts';

export const resetSessionScopedGuaranteesState = () => initialState;

export const hydrateGuaranteesSectionSlicesFromApplication = (
  state: TGuaranteesPageStore,
  application: TGuaranteesApplication
) => {
  if (application.principal) {
    setGuaranteesFulfilledAction(state.principal, application.principal);
  }

  if (application.beneficiary) {
    setGuaranteesFulfilledAction(
      state.beneficiary,
      normalizeGuaranteesBeneficiaryResponse(application.beneficiary)
    );
  }

  const baseRelations = {
    ...(application.baseRelations ?? {}),
    baseRelationsDocument:
      application.baseRelations?.baseRelationsDocument ?? application.baseRelationsDocument,
    principalObligationDetails:
      application.baseRelations?.principalObligationDetails ?? application.principalObligationDetails,
  };

  if (
    application.baseRelations ||
    application.baseRelationsDocument ||
    application.principalObligationDetails
  ) {
    setGuaranteesFulfilledAction(state.baseRelations, baseRelations);
  }

  if (application.guaranteeForm) {
    setGuaranteesFulfilledAction(state.guaranteeForm, application.guaranteeForm);
  }

  if (application.mainDealConditions) {
    setGuaranteesFulfilledAction(state.mainDealConditions, application.mainDealConditions);
  }

  if (application.managerTerms) {
    setGuaranteesFulfilledAction(
      state.managerTerms,
      application.managerTerms as TGuaranteesManagerTerms
    );
  }

  if (application.executionForm) {
    setGuaranteesFulfilledAction(
      state.executionForm,
      application.executionForm as TGuaranteesExecutionForm
    );
  }
};

export const handleFetchGuaranteesApplicationPending = (state: TGuaranteesPageStore) => {
  setGuaranteesPendingAction(state.currentApplication);
  state.currentApplication.data = null;
  state.fetchProzorroTender = initialState.fetchProzorroTender;
  state.applicationTenderInfo = initialState.applicationTenderInfo;
  state.patchApplicationMainDeal = initialState.patchApplicationMainDeal;
  state.principal = initialState.principal;
  state.patchPrincipal = initialState.patchPrincipal;
  state.beneficiary = initialState.beneficiary;
  state.patchBeneficiary = initialState.patchBeneficiary;
  state.baseRelations = initialState.baseRelations;
  state.patchBaseRelations = initialState.patchBaseRelations;
  state.guaranteeForm = initialState.guaranteeForm;
  state.patchGuaranteeForm = initialState.patchGuaranteeForm;
  state.mainDealConditions = initialState.mainDealConditions;
  state.patchMainDealConditions = initialState.patchMainDealConditions;
  state.managerTerms = initialState.managerTerms;
  state.patchManagerTerms = initialState.patchManagerTerms;
  state.executionForm = initialState.executionForm;
  state.patchExecutionForm = initialState.patchExecutionForm;
  state.patchIssueGuarantee = initialState.patchIssueGuarantee;
};

export const handleFetchGuaranteesApplicationFulfilled = (
  state: TGuaranteesPageStore,
  application: TGuaranteesApplication
) => {
  setGuaranteesFulfilledAction(state.currentApplication, application);
  hydrateGuaranteesSectionSlicesFromApplication(state, application);
};

export const synchronizeGuaranteesBeneficiaryResponse = (
  state: TGuaranteesPageStore,
  beneficiary: TGuaranteesBeneficiaryResponse
) => {
  const beneficiaries = preserveGuaranteesBeneficiaryCountryNames(
    beneficiary,
    state.currentApplication.data?.beneficiary
  );

  setGuaranteesFulfilledAction(state.beneficiary, beneficiaries);

  if (state.currentApplication.data) {
    state.currentApplication.data.beneficiary = beneficiaries;
  }
};

export const handlePatchGuaranteesPrincipalFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesPrincipal | TGuaranteesApplication
) => {
  setGuaranteesFulfilledAction(state.patchPrincipal, payload);

  if (isGuaranteesApplicationPayload(payload)) {
    setGuaranteesFulfilledAction(state.currentApplication, payload);
    hydrateGuaranteesSectionSlicesFromApplication(state, payload);
  } else {
    setGuaranteesFulfilledAction(state.principal, payload);
    updateCurrentGuaranteesApplicationVersion(state, payload);
  }
};

export const handlePatchGuaranteesBeneficiaryFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesBeneficiaryResponse | TGuaranteesApplication
) => {
  setGuaranteesFulfilledAction(state.patchBeneficiary, payload);

  if (isGuaranteesApplicationPayload(payload)) {
    setGuaranteesFulfilledAction(state.currentApplication, payload);
    hydrateGuaranteesSectionSlicesFromApplication(state, payload);
  } else {
    synchronizeGuaranteesBeneficiaryResponse(state, payload);

    updateCurrentGuaranteesApplicationVersion(state, payload);
  }
};

export const handlePatchGuaranteesBaseRelationsFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesBaseRelations | TGuaranteesApplication
) => {
  setGuaranteesFulfilledAction(state.patchBaseRelations, payload);

  if (isGuaranteesApplicationPayload(payload)) {
    setGuaranteesFulfilledAction(state.currentApplication, payload);
    hydrateGuaranteesSectionSlicesFromApplication(state, payload);
  } else {
    setGuaranteesFulfilledAction(state.baseRelations, payload);

    if (state.currentApplication.data) {
      state.currentApplication.data.baseRelations = payload;
    }

    updateCurrentGuaranteesApplicationVersion(state, payload);
  }
};

export const handlePatchGuaranteesFormFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesForm | TGuaranteesApplication
) => {
  setGuaranteesFulfilledAction(state.patchGuaranteeForm, payload);

  if (isGuaranteesApplicationPayload(payload)) {
    setGuaranteesFulfilledAction(state.currentApplication, payload);
    hydrateGuaranteesSectionSlicesFromApplication(state, payload);
  } else {
    setGuaranteesFulfilledAction(state.guaranteeForm, payload);

    if (state.currentApplication.data) {
      state.currentApplication.data.guaranteeForm = payload;
    }

    updateCurrentGuaranteesApplicationVersion(state, payload);
  }
};

export const handlePatchGuaranteesMainDealConditionsFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesMainDealConditions
) => {
  setGuaranteesFulfilledAction(state.patchMainDealConditions, payload);
  setGuaranteesFulfilledAction(state.mainDealConditions, payload);

  if (state.currentApplication.data) {
    state.currentApplication.data.mainDealConditions = payload;
  }

  updateCurrentGuaranteesApplicationVersion(state, payload);
};

export const handlePatchGuaranteesManagerTermsFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesManagerTerms | TGuaranteesApplication
) => {
  setGuaranteesFulfilledAction(state.patchManagerTerms, payload);

  if (isGuaranteesApplicationPayload(payload)) {
    setGuaranteesFulfilledAction(state.currentApplication, payload);
    hydrateGuaranteesSectionSlicesFromApplication(state, payload);
  } else {
    setGuaranteesFulfilledAction(state.managerTerms, payload);

    if (state.currentApplication.data) {
      state.currentApplication.data.managerTerms = payload;
    }

    updateCurrentGuaranteesApplicationVersion(state, payload);
  }
};

export const handlePatchGuaranteesExecutionFormFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesExecutionForm | TGuaranteesApplication
) => {
  setGuaranteesFulfilledAction(state.patchExecutionForm, payload);

  if (isGuaranteesApplicationPayload(payload)) {
    setGuaranteesFulfilledAction(state.currentApplication, payload);
    hydrateGuaranteesSectionSlicesFromApplication(state, payload);
  } else {
    setGuaranteesFulfilledAction(state.executionForm, payload);

    if (state.currentApplication.data) {
      state.currentApplication.data.executionForm = payload;
    }

    updateCurrentGuaranteesApplicationVersion(state, payload);
  }
};

export const handlePatchGuaranteesIssueGuaranteeFulfilled = (
  state: TGuaranteesPageStore,
  payload: TGuaranteesIssueGuaranteeDetails | TGuaranteesApplication | null
) => {
  setGuaranteesFulfilledAction(state.patchIssueGuarantee, payload);

  if (!payload) {
    return;
  }

  if (isGuaranteesApplicationPayload(payload)) {
    setGuaranteesFulfilledAction(state.currentApplication, payload);
    hydrateGuaranteesSectionSlicesFromApplication(state, payload);

    return;
  }

  if (state.currentApplication.data) {
    state.currentApplication.data.issueDetails = {
      ...(state.currentApplication.data.issueDetails ?? {}),
      ...payload,
    };
  }

  updateCurrentGuaranteesApplicationVersion(state, payload);
};

export const handleFetchGuaranteesAccountsPending = (state: TGuaranteesPageStore) => {
  setGuaranteesPendingAction(state.accounts);
  state.accounts.data = initialState.accounts.data;
};

export const handleFetchGuaranteesAccountsRejected = (
  state: TGuaranteesPageStore,
  payload: unknown
) => {
  setGuaranteesRejectedAction(state.accounts, payload);
  state.accounts.data = initialState.accounts.data;
};

export const handleFetchGuaranteesMainDealsPending = (
  state: TGuaranteesPageStore,
  request: TFetchGuaranteesMainDealsRequest
) => {
  state.mainDealsRequestKey = buildGuaranteesMainDealsRequestKey(request);
  setGuaranteesPendingAction(state.mainDeals);
  state.mainDeals.data = initialState.mainDeals.data;
};

export const handleFetchGuaranteesMainDealsRejected = (
  state: TGuaranteesPageStore,
  payload: unknown
) => {
  setGuaranteesRejectedAction(state.mainDeals, payload);
  state.mainDeals.data = initialState.mainDeals.data;
};
